/**
 * Economy, end-of-turn bookkeeping, and the full turn loop.
 * Ported from legacy/admirals-return.html endOfTurn()/finishGame()/endPlayerMovement(),
 * cross-checked against the mirrored manual:
 *   docs/reference/site/manual/game-rules.md and battle-walkthrough.md.
 *
 * A full game turn (game-rules.md "Each game turn consists of"):
 *   Red moves → combat (both sides fire) → Yellow moves → combat → end-of-turn
 *   (ownership/VP/resources, builds complete, repairs, new turn).
 *
 * Pure: no UI/audio. Mutates G and returns structured reports so the renderer
 * (WP-5/6) and fog announcements can react without re-deriving anything.
 */
import { hexDist } from './board';
import { collectCombatSites, type CombatEvent, type CombatSite } from './combat';
import { separateShips } from './deploy';
import { isTransportClass, UNITS, addShip } from './ships';
import { type City, type GameState, type Ship, type ShipType, type Side } from './state';

/** A city's owner changed, or it reverted to neutral this end-of-turn. */
export interface CaptureEvent {
  cityIndex: number;
  name: string;
  /** new owner; null when the city reverted to neutral (owner has no ship adjacent) */
  owner: Side | null;
  vpAwarded: number;
  /** captured enemy home port → VP doubled (game-rules.md) */
  doubled: boolean;
  /** reverted to neutral this turn (owner had no ship left adjacent) */
  neutralized: boolean;
  /** owner differs from the owner at the start of this end-of-turn */
  changedHands: boolean;
}

export interface BuildCompleteEvent {
  cityIndex: number;
  name: string;
  side: Side;
  shipId: number;
  type: Ship['type'];
}

export interface RepairEvent {
  shipId: number;
  amount: number;
}

export interface EndOfTurnReport {
  captures: CaptureEvent[];
  builds: BuildCompleteEvent[];
  repairs: RepairEvent[];
  /** VP added to each side this turn */
  vpGained: Record<Side, number>;
  /** running totals after this turn */
  vp: Record<Side, number>;
  /** the new turn number (after increment) */
  turn: number;
  over: boolean;
  winner: Side | null;
}

/** Live ships adjacent to a city (hexDist === 1) — cities are impassable land, so
 *  a fleet influences one only from the surrounding water tiles. */
function adjacentShips(g: GameState, c: number, r: number): Ship[] {
  return g.ships.filter((s) => s.hp > 0 && hexDist(s.c, s.r, c, r) === 1);
}

/**
 * Resolve all combat sites once (one combat round per contested hex), in a
 * stable order. `playerTargets` (optional) maps attacker id → chosen target id;
 * un-chosen / sunk targets fall back to the engine's default pick.
 */
export function resolveCombat(
  g: GameState,
  playerTargets?: Record<number, number>,
): CombatEvent[] {
  return collectCombatSites(g, playerTargets).flatMap((s) => s.events);
}

/**
 * Same combat phase as resolveCombat, but returns the per-site structure the
 * combat-viewer cinematic (WP-8) animates (pre-combat snapshot + events).
 */
export function resolveCombatSites(
  g: GameState,
  playerTargets?: Record<number, number>,
): CombatSite[] {
  return collectCombatSites(g, playerTargets);
}

/**
 * End-of-turn economy + bookkeeping. Mutates G; returns a structured report.
 * Order matches legacy/manual: ownership & VP → build completion → repair →
 * per-turn flag reset → turn++ (+ finishGame at the limit).
 */
export function endOfTurn(g: GameState): EndOfTurnReport {
  const gained: Record<Side, number> = { R: 0, Y: 0 };
  const captures: CaptureEvent[] = [];

  for (let i = 0; i < g.cities.length; i++) {
    const ct = g.cities[i]!;
    const prevOwner = ct.owner;
    const adj = adjacentShips(g, ct.c, ct.r);
    const rAny = adj.some((s) => s.side === 'R');
    const yAny = adj.some((s) => s.side === 'Y');
    const rT = adj.some((s) => s.side === 'R' && isTransportClass(s));
    const yT = adj.some((s) => s.side === 'Y' && isTransportClass(s));

    // Ownership (adjacency model, 2026-07-08):
    //  - CAPTURE requires an UNCONTESTED transport: a side with a TR/AT adjacent
    //    AND no enemy ship of any kind adjacent takes (or keeps) the city. If both
    //    sides have ships adjacent the flag can't flip — you must fight it out
    //    until only one side surrounds it (only one branch can fire: !yAny and
    //    !rAny are mutually exclusive when both sides have a transport).
    //  - Otherwise, a city reverts to NEUTRAL the moment its owner has no ship of
    //    any kind adjacent; a contested owner (enemy also adjacent) is retained.
    if (rT && !yAny) ct.owner = 'R';
    else if (yT && !rAny) ct.owner = 'Y';
    else if (ct.owner && (ct.owner === 'R' ? !rAny : !yAny)) ct.owner = null;

    // Scoring: the owner collects its VP whenever it has a transport adjacent —
    // even if an enemy ship contests the city (contest blocks capture, not income).
    const owner = ct.owner;
    const ownerT = owner === 'R' ? rT : owner === 'Y' ? yT : false;
    let vpAwarded = 0;
    let doubled = false;
    if (owner && ownerT) {
      doubled = ct.home != null && ct.home !== owner;
      vpAwarded = doubled ? ct.vp * 2 : ct.vp;
      g.vp[owner] += vpAwarded;
      gained[owner] += vpAwarded;
    }
    const changedHands = ct.owner !== prevOwner;
    if (changedHands || vpAwarded > 0) {
      captures.push({
        cityIndex: i, name: ct.name, owner: ct.owner, vpAwarded, doubled,
        neutralized: ct.owner === null && prevOwner !== null, changedHands,
      });
    }
  }

  // Builds are now instant purchases at a shipyard (see purchaseShip), spent
  // straight from the points pool during the turn — there is no end-of-turn
  // build queue any more, so nothing completes here.
  const builds: BuildCompleteEvent[] = [];

  // Repair: a ship ending its turn ADJACENT to a friendly-owned city heals up to
  // 2 HP (cities are impassable land, so port repair — like capture — is by
  // adjacency to the harbour rather than sitting on it).
  const repairs: RepairEvent[] = [];
  for (const s of g.ships) {
    const max = UNITS[s.type].hp;
    if (s.hp >= max) continue;
    const nearFriendlyCity = g.cities.some(
      (ct) => ct.owner === s.side && hexDist(s.c, s.r, ct.c, ct.r) === 1,
    );
    if (nearFriendlyCity) {
      const amount = Math.min(2, max - s.hp);
      s.hp += amount;
      repairs.push({ shipId: s.id, amount });
    }
  }

  // Per-turn flag reset. anchored persists (manual: D toggles until un-anchored).
  for (const s of g.ships) {
    s.moved = 0;
    s.done = false;
    s.fought = false;
  }

  g.turn++;
  if (g.turn > g.maxTurn) finishGame(g);

  return {
    captures, builds, repairs,
    vpGained: gained,
    vp: { R: g.vp.R, Y: g.vp.Y },
    turn: g.turn,
    over: g.phase === 'over',
    winner: g.winner,
  };
}

/**
 * Decide the winner at the battle's end. The favored side must out-score the
 * other by at least `ratio` percent, else the other side wins (game-rules.md
 * "Win Ratio"). Ported from legacy finishGame().
 */
export function finishGame(g: GameState): void {
  g.phase = 'over';
  const f = g.favored;
  const o: Side = f === 'R' ? 'Y' : 'R';
  const need = g.vp[o] * (1 + g.ratio / 100);
  g.winner = g.vp[f] >= need ? f : o;
}

export interface VpProjection {
  /** the favored side, which must clear the win ratio */
  favored: Side;
  other: Side;
  /** required win ratio, in percent */
  ratio: number;
  /** favored side's current lead over the other, in percent (rounded) */
  marginPct: number;
  /** who wins if the battle ended now */
  projectedWinner: Side;
  /** marginPct − ratio (rounded): ≥0 = over-win cushion, <0 = how far short */
  overWin: number;
}

/**
 * Project the current standing onto the win condition. The favored side must
 * out-score the other by `ratio` percent (game-rules.md "Win Ratio"); this
 * reports the current margin and who would win if the battle ended now. Pure —
 * drives the HUD prediction line and the game-over stats, identical math to
 * finishGame().
 */
export function vpProjection(g: GameState): VpProjection {
  const f = g.favored;
  const o: Side = f === 'R' ? 'Y' : 'R';
  const marginPct = Math.round((g.vp[f] / Math.max(1, g.vp[o]) - 1) * 100);
  const need = g.vp[o] * (1 + g.ratio / 100);
  return {
    favored: f,
    other: o,
    ratio: g.ratio,
    marginPct,
    projectedWinner: g.vp[f] >= need ? f : o,
    overWin: marginPct - g.ratio,
  };
}

/**
 * Instantly purchase a unit at a shipyard (a 200-point city the side owns),
 * spending its cost from the side's points pool. The new ship appears at the
 * shipyard — or the nearest free water tile if it's occupied (one per tile).
 * Returns false (no mutation) if the city is the wrong type, not owned by
 * `side`, or the side can't afford it. Single source of truth for builds — the
 * AI and the build menu both go through here.
 */
export function purchaseShip(g: GameState, city: City, type: ShipType, side: Side): boolean {
  if (city.vp !== 200 || city.owner !== side) return false;
  const cost = UNITS[type].cost;
  if (g.vp[side] < cost) return false;
  g.vp[side] -= cost;
  addShip(g, side, type, city.c, city.r);
  separateShips(g); // keep one ship per tile if the shipyard was occupied
  return true;
}

/** A side's movement: select/move/anchor its ships however it likes (AI or scripted). */
export type MoveFn = (g: GameState, side: Side) => void;

/** Optional per-side target chooser for combat (attacker id → target id). */
export type TargetFn = (g: GameState, side: Side) => Record<number, number> | undefined;

export interface TurnReport {
  redCombat: CombatEvent[];
  yellowCombat: CombatEvent[];
  endOfTurn: EndOfTurnReport;
}

/**
 * Run one full game turn through the rules API. Both fleets move, THEN combat is
 * resolved once — the "end of turn, both sides" model: adjacent enemy pairs fight
 * a single mutual round after all movement. Resolving once (rather than after
 * each side) avoids exposing the first mover's ships to two combat rounds while
 * the second mover retreats between them. The side that moves first still
 * alternates by turn parity so neither reaps a permanent reaction advantage.
 *
 * Headless full-game callers loop runTurn until report.endOfTurn.over.
 */
export function runTurn(
  g: GameState,
  moveRed: MoveFn,
  moveYellow: MoveFn = moveRed,
  targets?: TargetFn,
): TurnReport {
  // First mover alternates each turn AND flips with the seed, so neither side
  // systematically owns turn 1 — whose reaction advantage on that turn (grab the
  // best neutral cities second) compounds through the game's per-turn income.
  const rFirst = (g.turn + g.seed) % 2 === 0;
  const order: Side[] = rFirst ? ['R', 'Y'] : ['Y', 'R'];
  for (const side of order) {
    g.active = side;
    g.phase = 'move';
    (side === 'R' ? moveRed : moveYellow)(g, side);
  }
  g.phase = 'combat';
  const tR = targets?.(g, 'R');
  const tY = targets?.(g, 'Y');
  const merged = tR || tY ? { ...tR, ...tY } : undefined;
  const events = resolveCombat(g, merged);
  const eot = endOfTurn(g);
  // events carries the whole (single) combat phase; split by side for the report.
  return {
    redCombat: events.filter((e) => e.attackerSide === 'R'),
    yellowCombat: events.filter((e) => e.attackerSide === 'Y'),
    endOfTurn: eot,
  };
}
